/**
 * Weekly aggregations and rule-based root-cause hypothesis engine for Colo vs Azure
 * migration performance. Pure functions — consumes the `series` / `summary` / `topErrors`
 * payload returned by /api/migrations/azure-migration-performance.
 *
 * Design goals:
 *   - No new Datadog queries: everything is derived from existing per-metric timeseries.
 *   - Deterministic and testable: no Date.now(), no side effects.
 *   - Transparent: every hypothesis carries its evidence list (metric, week, numeric delta).
 */

import { pctDelta, verdictLowerBetter, verdictHigherBetter } from './azureMigrationComparisonUtils';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Shared metric config — kept in one place so the weekly table, root-cause engine
 * and the existing scorecards stay consistent. Units are applied in formatting only.
 */
export const METRIC_CONFIGS = [
  {
    key: 'p99',
    label: 'Inbound p99',
    shortLabel: 'p99',
    seriesKey: 'servletLatencyP99',
    mult: 1000,
    unit: 'ms',
    decimals: 1,
    lowerBetter: true,
    similarBandPct: 20,
    // Minimum colo baseline below which % change is unreliable (0.001 s = 1 ms).
    minMeaningfulBase: 0.001
  },
  {
    key: 'http',
    label: 'Outbound HTTP',
    shortLabel: 'Outbound',
    seriesKey: 'httpClientDuration',
    mult: 1000,
    unit: 'ms',
    decimals: 1,
    lowerBetter: true,
    similarBandPct: 15,
    minMeaningfulBase: 0.001
  },
  {
    key: 'err',
    label: 'Error rate',
    shortLabel: 'Errors',
    seriesKey: 'errorRate',
    mult: 1,
    unit: '%',
    decimals: 3,
    lowerBetter: true,
    similarBandPct: 25,
    // Error rate is stored as a percentage value; anything below 0.001% is effectively zero.
    minMeaningfulBase: 0.001
  },
  {
    key: 'mem',
    label: 'Memory usable',
    shortLabel: 'Memory',
    seriesKey: 'hostMemPctUsable',
    mult: 1,
    unit: '%',
    decimals: 2,
    lowerBetter: false,
    similarBandPct: 10,
    minMeaningfulBase: 1
  }
];

export const METRIC_CONFIG_BY_KEY = METRIC_CONFIGS.reduce((acc, m) => {
  acc[m.key] = m;
  return acc;
}, {});

/** Format a per-metric value (e.g. 0.123 servlet seconds → "123.0 ms"). */
export function formatMetricValue(metricKey, value) {
  const cfg = METRIC_CONFIG_BY_KEY[metricKey];
  if (!cfg) return value == null ? '—' : String(value);
  if (value == null || !Number.isFinite(value)) return '—';
  const scaled = value * cfg.mult;
  const num = scaled.toFixed(cfg.decimals);
  return cfg.unit ? `${num} ${cfg.unit}` : num;
}

/** Monday-anchored UTC week start (milliseconds). */
export function weekStartUtc(ts) {
  if (ts == null || !Number.isFinite(ts)) return null;
  const d = new Date(ts);
  const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dow = new Date(dayStart).getUTCDay(); // 0 = Sun
  const mondayOffset = (dow + 6) % 7; // 0 for Monday
  return dayStart - mondayOffset * DAY_MS;
}

function formatWeekLabel(startMs) {
  if (startMs == null) return '';
  const d = new Date(startMs);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `Wk of ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

/**
 * Build the list of weeks covering [fromMs, toMs]. Anchors to Monday UTC weeks.
 * Flags the cutover week (containing migrationMs) and pre/post-cutover partition.
 *
 * @returns {Array<{ weekStart:number, weekEnd:number, weekLabel:string,
 *   isCutoverWeek:boolean, isPreMigration:boolean, isPostMigration:boolean }>}
 */
export function buildWeeks(fromMs, toMs, migrationMs = null) {
  if (fromMs == null || toMs == null || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
    return [];
  }
  const firstWeek = weekStartUtc(fromMs);
  if (firstWeek == null) return [];
  const weeks = [];
  let cursor = firstWeek;
  const cutoverWeek = migrationMs != null ? weekStartUtc(migrationMs) : null;
  // Cap at 64 weeks to avoid runaway loops.
  let safety = 0;
  while (cursor <= toMs && safety++ < 64) {
    const weekEnd = cursor + WEEK_MS - 1;
    const isCutoverWeek = cutoverWeek != null && cursor === cutoverWeek;
    const isPreMigration = migrationMs != null && weekEnd < migrationMs;
    const isPostMigration = migrationMs != null && cursor > migrationMs;
    weeks.push({
      weekStart: cursor,
      weekEnd,
      weekLabel: formatWeekLabel(cursor),
      isCutoverWeek,
      isPreMigration,
      isPostMigration
    });
    cursor += WEEK_MS;
  }
  return weeks;
}

function avgFinite(values) {
  if (!values || !values.length) return null;
  let sum = 0;
  let count = 0;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    sum += v;
    count += 1;
  }
  if (count === 0) return null;
  return sum / count;
}

/**
 * Bucket a points array (`[{t, v}]`) into the provided weeks, returning aligned averages.
 */
function aggregatePointsByWeeks(points, weeks) {
  const out = weeks.map(() => []);
  if (!Array.isArray(points) || !points.length || !weeks.length) {
    return weeks.map(() => ({ avg: null, count: 0 }));
  }
  // Linear scan; weeks are ordered ascending.
  let wi = 0;
  const sorted = [...points].sort((a, b) => a.t - b.t);
  for (const p of sorted) {
    if (p.v == null || !Number.isFinite(p.v) || p.t == null) continue;
    while (wi < weeks.length && p.t > weeks[wi].weekEnd) wi += 1;
    if (wi >= weeks.length) break;
    if (p.t >= weeks[wi].weekStart) out[wi].push(p.v);
  }
  return out.map((bucket) => ({
    avg: avgFinite(bucket),
    count: bucket.length
  }));
}

/**
 * Per-metric per-week stats with Azure − Colo delta and verdict.
 *
 * Because Colo data only exists before the migration cutover and Azure data only exists
 * after, a naive "both envs must have data this week" check would produce blanks for
 * every week except the cutover week itself.
 *
 * Instead we use a period-average baseline:
 *   - Pre-cutover weeks:  compare [this week's Colo avg] vs [Azure's full-period avg]
 *   - Post-cutover weeks: compare [Colo's full-period avg] vs [this week's Azure avg]
 *   - Cutover week / both present: use both real weekly averages
 *
 * The `comparisonMode` field tells the UI which side is the live weekly value and which
 * is the baseline reference, so tooltips can label them correctly.
 *
 * @returns {Array<{
 *   weekStart, weekEnd, weekLabel, isCutoverWeek, isPreMigration, isPostMigration,
 *   colo, azure, coloCount, azureCount, deltaPct, verdict, hasBothEnvs,
 *   comparisonMode: 'live'|'colo-vs-azure-baseline'|'colo-baseline-vs-azure'|'no-data'
 * }>}
 */
export function computeMetricWeeklyStats(metricKey, coloPoints, azurePoints, weeks) {
  const cfg = METRIC_CONFIG_BY_KEY[metricKey];
  if (!cfg || !weeks.length) return [];

  const coloBuckets = aggregatePointsByWeeks(coloPoints, weeks);
  const azureBuckets = aggregatePointsByWeeks(azurePoints, weeks);

  // Full-period averages — used as the baseline for single-env weeks.
  const coloAllVals = (coloPoints || []).filter((p) => p.v != null && Number.isFinite(p.v)).map((p) => p.v);
  const azureAllVals = (azurePoints || []).filter((p) => p.v != null && Number.isFinite(p.v)).map((p) => p.v);
  const coloPeriodAvg = avgFinite(coloAllVals);
  const azurePeriodAvg = avgFinite(azureAllVals);

  return weeks.map((w, i) => {
    const coloWeekly = coloBuckets[i].avg;
    const azureWeekly = azureBuckets[i].avg;
    const coloCount = coloBuckets[i].count;
    const azureCount = azureBuckets[i].count;

    let colo, azure, comparisonMode;

    if (coloWeekly != null && azureWeekly != null) {
      // Both envs have real data this week (e.g. cutover week or an overlap).
      colo = coloWeekly;
      azure = azureWeekly;
      comparisonMode = 'live';
    } else if (coloWeekly != null && azurePeriodAvg != null) {
      // Pre-migration week: compare this week's Colo against Azure's period average.
      colo = coloWeekly;
      azure = azurePeriodAvg;
      comparisonMode = 'colo-vs-azure-baseline';
    } else if (azureWeekly != null && coloPeriodAvg != null) {
      // Post-migration week: compare Colo's period average against this week's Azure.
      colo = coloPeriodAvg;
      azure = azureWeekly;
      comparisonMode = 'colo-baseline-vs-azure';
    } else {
      colo = coloWeekly;
      azure = azureWeekly;
      comparisonMode = 'no-data';
    }

    // Suppress % change when the Colo baseline is near-zero — the formula
    // ((azure - colo) / colo) * 100 explodes when colo ≈ 0 and produces
    // figures like +800% that are technically correct but completely misleading.
    const isBaselineNearZero =
      colo != null &&
      cfg.minMeaningfulBase != null &&
      Math.abs(colo) < cfg.minMeaningfulBase;

    const deltaPct = isBaselineNearZero ? null : pctDelta(colo, azure);

    // Absolute difference (in raw units, before mult) — always available when
    // both sides exist, used as a fallback when % is suppressed.
    const deltaAbs =
      colo != null && azure != null && Number.isFinite(colo) && Number.isFinite(azure)
        ? azure - colo
        : null;

    const verdict =
      colo != null && azure != null && !isBaselineNearZero
        ? cfg.lowerBetter
          ? verdictLowerBetter(colo, azure, cfg.similarBandPct)
          : verdictHigherBetter(colo, azure, cfg.similarBandPct)
        : { label: '—', tone: 'neutral' };

    return {
      ...w,
      colo,
      azure,
      coloCount,
      azureCount,
      deltaPct,
      deltaAbs,
      isBaselineNearZero,
      verdict,
      comparisonMode,
      hasBothEnvs: colo != null && azure != null,
    };
  });
}

/**
 * Compute weekly stats for every metric in METRIC_CONFIGS from the payload series.
 *
 * @param {object} params
 * @param {object} params.series - payload.series from the performance API
 * @param {object} params.meta - payload.meta (from / to / azureMigrationDate)
 * @returns {{ weeks:Array, byMetric:Object<string,Array>, weekCount:number, hasMigrationInRange:boolean }}
 */
export function computeWeeklyStats({ series, meta }) {
  if (!series || !meta) {
    return { weeks: [], byMetric: {}, weekCount: 0, hasMigrationInRange: false };
  }
  const fromMs = meta.from ? new Date(meta.from).getTime() : null;
  const toMs = meta.to ? new Date(meta.to).getTime() : null;
  const migrationMs = meta.azureMigrationDate ? new Date(meta.azureMigrationDate).getTime() : null;
  const hasMigrationInRange =
    migrationMs != null && fromMs != null && toMs != null && migrationMs >= fromMs && migrationMs <= toMs;
  const weeks = buildWeeks(fromMs, toMs, hasMigrationInRange ? migrationMs : null);
  const byMetric = {};
  for (const cfg of METRIC_CONFIGS) {
    const s = series[cfg.seriesKey];
    if (!s) {
      byMetric[cfg.key] = weeks.map((w) => ({
        ...w,
        colo: null,
        azure: null,
        coloCount: 0,
        azureCount: 0,
        deltaPct: null,
        verdict: { label: '—', tone: 'neutral' },
        hasBothEnvs: false
      }));
      continue;
    }
    byMetric[cfg.key] = computeMetricWeeklyStats(cfg.key, s.colo || [], s.azure || [], weeks);
  }
  return { weeks, byMetric, weekCount: weeks.length, hasMigrationInRange };
}

// ---------------------------------------------------------------------------
// Root-cause hypothesis engine
// ---------------------------------------------------------------------------

function finiteOrNull(v) {
  return v != null && Number.isFinite(v) ? v : null;
}

function countBadWeeks(metricWeeks) {
  if (!metricWeeks) return 0;
  return metricWeeks.filter((w) => w.hasBothEnvs && w.verdict?.tone === 'bad').length;
}

function worstWeekFor(metricWeeks) {
  if (!metricWeeks || !metricWeeks.length) return null;
  return [...metricWeeks]
    .filter((w) => w.hasBothEnvs && w.deltaPct != null)
    .sort((a, b) => Math.abs(b.deltaPct) - Math.abs(a.deltaPct))[0] || null;
}

function fmtPctSign(n, fractionDigits = 1) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(fractionDigits)}%`;
}

/**
 * Build a handful of ranked root-cause hypotheses from the weekly stats + period averages.
 * Each hypothesis includes structured evidence so the UI can render it transparently.
 *
 * @param {object} params
 * @param {{ weeks:Array, byMetric:Object, hasMigrationInRange:boolean }} params.weekly
 * @param {object} params.summary - payload.summary
 * @param {object} params.topErrors - payload.topErrors
 * @param {object} params.meta - payload.meta
 * @returns {Array<{
 *   id:string, label:string, confidence:'high'|'medium'|'low',
 *   summary:string, evidence:string[], nextSteps:string[], tone:'bad'|'watch'|'neutral'
 * }>}
 */
export function computeRootCauseHypotheses({ weekly, summary, topErrors, meta }) {
  const hypotheses = [];
  if (!weekly || !summary) return hypotheses;
  const { byMetric, hasMigrationInRange } = weekly;
  const c = summary.colo || {};
  const a = summary.azure || {};

  // Period-level deltas (percent). Inbound *average* latency (servletSeconds) has been
  // retired from this report — the Apache access-log split owns that story now, and p99
  // remains as the in-app tail signal (the best "did Azure handle the load?" indicator).
  const dP99 = pctDelta(c.servletP99Seconds, a.servletP99Seconds);
  const dHttp = pctDelta(c.httpSeconds, a.httpSeconds);
  const dErr = pctDelta(c.errorRatePct, a.errorRatePct);
  const dCpu = pctDelta(c.cpuUserPct, a.cpuUserPct);
  const dMem = pctDelta(c.memPctUsable, a.memPctUsable);

  const p99BadWeeks = countBadWeeks(byMetric.p99);
  const httpBadWeeks = countBadWeeks(byMetric.http);
  const errBadWeeks = countBadWeeks(byMetric.err);
  const cpuBadWeeks = countBadWeeks(byMetric.cpu);
  const memBadWeeks = countBadWeeks(byMetric.mem);

  const worstP99 = worstWeekFor(byMetric.p99);
  const worstHttp = worstWeekFor(byMetric.http);
  const worstErr = worstWeekFor(byMetric.err);
  const worstCpu = worstWeekFor(byMetric.cpu);
  const worstMem = worstWeekFor(byMetric.mem);

  const oldEnv = meta?.oldEnv || 'Colo';
  const newEnv = meta?.newEnv || 'Azure';

  const pushHypothesis = (h) => hypotheses.push(h);

  // ---- 1) Tail-latency regression (p99). The strongest "did Azure handle the load?"
  // signal we have: averages can mask a handful of 10×-slower requests that p99 exposes.
  if (finiteOrNull(dP99) != null && dP99 > 25) {
    const conf = p99BadWeeks >= 3 || dP99 > 60 ? 'high' : 'medium';
    pushHypothesis({
      id: 'tail-latency-regression',
      label: 'Tail-latency regression on Azure (p99 slower)',
      confidence: conf,
      tone: 'bad',
      summary: `Worst-case inbound latency is worse on ${newEnv}: p99 Δ ${fmtPctSign(dP99)} vs ${oldEnv}. The slowest 1% of requests got meaningfully slower — the clearest signal of capacity, GC or dependency-timeout pressure after the move, even if the average looks close.`,
      evidence: [
        `p99 period avg: ${formatMetricValue('p99', c.servletP99Seconds)} → ${formatMetricValue('p99', a.servletP99Seconds)} (Δ ${fmtPctSign(dP99)}).`,
        worstP99 ? `Worst p99 week: ${worstP99.weekLabel} (Δ ${fmtPctSign(worstP99.deltaPct)}).` : null,
        'Cross-check the Apache access-log section (real-user edge latency) for whether the tail regression is localised to the JVM (APM-only) or also visible at the edge (Apache-too).'
      ].filter(Boolean),
      nextSteps: [
        `In Datadog APM → Service → Resources, filter by env:${newEnv} and sort by p99; open the slowest traces and expand spans.`,
        'Check JVM GC time, thread-pool queue depth, and CPU throttling around the worst week — tail regressions usually correlate with one of these.',
        'Compare p99 between envs grouped by resource_name; a single heavy endpoint can dominate the tail.'
      ]
    });
  }

  // ---- 2) Dependency / network latency (outbound HTTP dominates).
  // We used to cross-check against inbound avg ("downstream took more time than the
  // servlet got slower"); now we use p99 as a proxy for the in-app signal. Outbound
  // slower than inbound p99 still implies the extra time is spent downstream.
  const outboundSignificant = finiteOrNull(dHttp) != null && dHttp > 20;
  const inboundMuchSmaller =
    finiteOrNull(dP99) == null || dP99 < 10 || (finiteOrNull(dHttp) != null && dHttp - (dP99 || 0) > 15);
  if (outboundSignificant && (inboundMuchSmaller || httpBadWeeks >= 2)) {
    const conf = httpBadWeeks >= 3 || dHttp > 40 ? 'high' : 'medium';
    pushHypothesis({
      id: 'dependency-network',
      label: 'Dependency or network path slower from Azure',
      confidence: conf,
      tone: 'bad',
      summary: `Outbound HTTP client latency is materially slower on ${newEnv} (${fmtPctSign(dHttp)} vs ${oldEnv}) while inbound p99 is ${
        finiteOrNull(dP99) != null ? fmtPctSign(dP99) : 'unchanged'
      }. Most of the added time is spent calling downstream services.`,
      evidence: [
        `Outbound HTTP period avg: ${formatMetricValue('http', c.httpSeconds)} → ${formatMetricValue('http', a.httpSeconds)} (Δ ${fmtPctSign(dHttp)}).`,
        httpBadWeeks > 0
          ? `${httpBadWeeks} of ${byMetric.http?.length ?? 0} weeks flagged Azure-worse for outbound HTTP.`
          : 'Outbound HTTP worse on the period average, even if weekly signal is mixed.',
        worstHttp
          ? `Worst week: ${worstHttp.weekLabel} (Δ ${fmtPctSign(worstHttp.deltaPct)}, ${oldEnv} ${formatMetricValue('http', worstHttp.colo)} → ${newEnv} ${formatMetricValue('http', worstHttp.azure)}).`
          : null,
        finiteOrNull(dP99) != null
          ? `Inbound p99 delta over same window: ${fmtPctSign(dP99)} — smaller than outbound, so the extra time is downstream.`
          : null
      ].filter(Boolean),
      nextSteps: [
        `In Datadog APM → Service map, compare edge latency from ${newEnv} vs ${oldEnv} to each downstream (DB, identity, partner APIs).`,
        'Check DNS / TLS / proxy hops and Azure regional routing to dependencies; look for connection-pool exhaustion or new retry behaviour.',
        'If DB is a dependency, compare jdbc/postgres span latency by peer service between environments.'
      ]
    });
  }

  // ---- 3) App runtime / compute bottleneck — inbound p99 + CPU move together.
  // Using p99 (instead of the retired inbound avg) is actually a sharper signal for
  // compute contention because the tail is where queueing / GC / CPU-steal show up
  // first; a regression visible in p99 + CPU is classic app-tier saturation.
  const inboundSignificant = finiteOrNull(dP99) != null && dP99 > 20;
  const cpuHigher = finiteOrNull(dCpu) != null && dCpu > 20;
  if (inboundSignificant && cpuHigher) {
    const conf = p99BadWeeks >= 3 && cpuBadWeeks >= 2 ? 'high' : 'medium';
    pushHypothesis({
      id: 'compute-bottleneck',
      label: 'App-tier compute / JVM bottleneck on Azure',
      confidence: conf,
      tone: 'bad',
      summary: `Inbound p99 latency is ${fmtPctSign(dP99)} worse and host CPU user is ${fmtPctSign(dCpu)} higher on ${newEnv}. Pattern typical of undersized compute, GC pressure or noisy neighbour — not a dependency issue.`,
      evidence: [
        `Inbound p99 period avg: ${formatMetricValue('p99', c.servletP99Seconds)} → ${formatMetricValue('p99', a.servletP99Seconds)} (Δ ${fmtPctSign(dP99)}).`,
        `Host CPU user period avg: ${formatMetricValue('cpu', c.cpuUserPct)} → ${formatMetricValue('cpu', a.cpuUserPct)} (Δ ${fmtPctSign(dCpu)}).`,
        worstP99
          ? `Worst p99 week: ${worstP99.weekLabel} (Δ ${fmtPctSign(worstP99.deltaPct)}).`
          : null,
        worstCpu
          ? `Worst CPU week: ${worstCpu.weekLabel} (Δ ${fmtPctSign(worstCpu.deltaPct)}).`
          : null
      ].filter(Boolean),
      nextSteps: [
        'Validate VM size / App Service plan vs previous Colo capacity; right-size if CPU is consistently pegged.',
        'In Datadog APM profiling (or runtime metrics) check GC time, thread pool queue depth, JIT warm-up and JVM heap/GC settings.',
        'Compare top resources by latency between envs — heavier request mix can also surface as inbound-only slowdown.'
      ]
    });
  }

  // ---- 4) Memory / GC pressure.
  const memWorse = finiteOrNull(dMem) != null && dMem < -10; // lower pct_usable is worse
  if (memWorse && (inboundSignificant || errBadWeeks >= 2)) {
    const conf = memBadWeeks >= 2 ? 'medium' : 'low';
    pushHypothesis({
      id: 'memory-pressure',
      label: 'Memory / GC pressure on Azure hosts',
      confidence: conf,
      tone: 'bad',
      summary: `Host usable memory on ${newEnv} is ${fmtPctSign(dMem)} vs ${oldEnv} (lower = tighter headroom), coinciding with ${
        inboundSignificant ? 'slower inbound p99 latency' : 'elevated errors'
      }. Likely heap/GC pressure or smaller RAM SKU.`,
      evidence: [
        `Memory usable period avg: ${formatMetricValue('mem', c.memPctUsable)} → ${formatMetricValue('mem', a.memPctUsable)} (Δ ${fmtPctSign(dMem)}).`,
        worstMem ? `Worst memory week: ${worstMem.weekLabel} (Δ ${fmtPctSign(worstMem.deltaPct)}).` : null,
        inboundSignificant ? `Inbound p99 Δ ${fmtPctSign(dP99)} over the same period.` : null,
        errBadWeeks > 0 ? `${errBadWeeks} week(s) also flagged higher error rate on ${newEnv}.` : null
      ].filter(Boolean),
      nextSteps: [
        'Compare JVM heap used vs max and GC pause time between envs; look for new OOM / full-GC patterns in logs.',
        'Check container memory limits and host RAM on the Azure SKU; increase if headroom is consistently < 20%.',
        'Rule out memory leaks introduced with the migration (new caches, changed pool sizes, larger batch jobs).'
      ]
    });
  }

  // ---- 5) Error regression.
  if (finiteOrNull(dErr) != null && dErr > 50 && finiteOrNull(a.errorRatePct) != null && a.errorRatePct > 0.05) {
    const conf = errBadWeeks >= 3 || dErr > 200 ? 'high' : 'medium';
    const azureTopStatus = topErrors?.azure?.byHttpStatus?.[0];
    const azureTopResource = topErrors?.azure?.byResource?.[0];
    const coloTopStatus = topErrors?.colo?.byHttpStatus?.[0];
    pushHypothesis({
      id: 'error-regression',
      label: 'Error regression on Azure',
      confidence: conf,
      tone: 'bad',
      summary: `Error rate on ${newEnv} is ${fmtPctSign(dErr)} vs ${oldEnv} over the window. Drill into the top statuses and resources below to see whether this is an application-logic or dependency-failure regression.`,
      evidence: [
        `Error rate period avg: ${c.errorRatePct != null ? c.errorRatePct.toFixed(3) : '—'}% → ${a.errorRatePct != null ? a.errorRatePct.toFixed(3) : '—'}% (Δ ${fmtPctSign(dErr)}).`,
        worstErr ? `Worst error week: ${worstErr.weekLabel} (Δ ${fmtPctSign(worstErr.deltaPct)}).` : null,
        azureTopStatus
          ? `Top status on ${newEnv}: ${azureTopStatus.label} (${azureTopStatus.count} events, ${azureTopStatus.pctOfErrors}% of env errors).`
          : null,
        coloTopStatus
          ? `For reference, top status on ${oldEnv}: ${coloTopStatus.label} (${coloTopStatus.count} events).`
          : null,
        azureTopResource
          ? `Top resource on ${newEnv}: ${azureTopResource.label} (${azureTopResource.count} events).`
          : null
      ].filter(Boolean),
      nextSteps: [
        `In Datadog APM, filter traces with error=true for service + env:${newEnv}; group by resource_name and http.status_code.`,
        'Correlate error spikes with recent deploys, secrets/config rotations, and dependency incidents on the same weeks.',
        'Verify auth / cert / feature-flag differences between envs if top status is 401/403/5xx.'
      ]
    });
  }

  // ---- 6) Cutover still in progress (post-migration trend improving).
  // Tracks p99 week-over-week since cutover (was inbound avg before the avg metric was
  // retired). p99 is a slightly harsher signal but tracks "warm-up" just as well.
  if (hasMigrationInRange && byMetric.p99?.length >= 2) {
    const postWeeks = byMetric.p99.filter((w) => w.isPostMigration && w.hasBothEnvs);
    if (postWeeks.length >= 2) {
      const first = postWeeks[0];
      const last = postWeeks[postWeeks.length - 1];
      if (
        finiteOrNull(first.deltaPct) != null &&
        finiteOrNull(last.deltaPct) != null &&
        first.deltaPct > 15 &&
        last.deltaPct < first.deltaPct - 10
      ) {
        pushHypothesis({
          id: 'cutover-stabilising',
          label: 'Post-cutover stabilisation in progress',
          confidence: 'medium',
          tone: 'watch',
          summary: `Inbound p99 latency on ${newEnv} has been improving week-over-week since cutover (${fmtPctSign(first.deltaPct)} in ${first.weekLabel} → ${fmtPctSign(last.deltaPct)} in ${last.weekLabel}). Consistent with warm-up / tuning after migration.`,
          evidence: [
            `First post-cutover week: ${first.weekLabel} — Azure Δ ${fmtPctSign(first.deltaPct)}.`,
            `Latest post-cutover week: ${last.weekLabel} — Azure Δ ${fmtPctSign(last.deltaPct)}.`,
            `Total post-cutover weeks observed: ${postWeeks.length}.`
          ],
          nextSteps: [
            'Re-assess after another full week; if the improving trend continues, no action needed.',
            'If trend flattens above baseline, apply the compute / dependency investigations from the other hypotheses.'
          ]
        });
      }
    }
  }

  // ---- 7) No material regression detected.
  const anyBad = hypotheses.some((h) => h.tone === 'bad');
  if (!anyBad) {
    pushHypothesis({
      id: 'no-regression',
      label: 'No clear performance regression detected',
      confidence: 'medium',
      tone: 'neutral',
      summary: `None of the monitored metrics show a material Azure-vs-Colo gap across the window. Validate the weekly table below and spot-check the top errors section to confirm.`,
      evidence: [
        finiteOrNull(dP99) != null ? `Inbound p99 Δ ${fmtPctSign(dP99)}.` : null,
        finiteOrNull(dHttp) != null ? `Outbound HTTP Δ ${fmtPctSign(dHttp)}.` : null,
        finiteOrNull(dErr) != null ? `Error rate Δ ${fmtPctSign(dErr)}.` : null,
        finiteOrNull(dCpu) != null ? `CPU Δ ${fmtPctSign(dCpu)}.` : null,
        finiteOrNull(dMem) != null ? `Memory usable Δ ${fmtPctSign(dMem)}.` : null
      ].filter(Boolean),
      nextSteps: [
        'Scan the weekly comparison table for any single-week outliers that period averages might hide.',
        'If business signals disagree with this view, extend the window or compare specific endpoints in Datadog APM.'
      ]
    });
  }

  // Rank: bad > watch > neutral, then by confidence high > medium > low.
  const toneRank = { bad: 0, watch: 1, neutral: 2 };
  const confRank = { high: 0, medium: 1, low: 2 };
  hypotheses.sort((x, y) => {
    const t = (toneRank[x.tone] ?? 3) - (toneRank[y.tone] ?? 3);
    if (t !== 0) return t;
    return (confRank[x.confidence] ?? 3) - (confRank[y.confidence] ?? 3);
  });

  return hypotheses;
}
